<template>
  <div class="grid grid-cols-1 lg:grid-cols-detail m-4 md:mx-8 md:mt-16 gap-8">
    <section class="w-full rounded-t">
      <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
        <h2>{{ $t("lang.msg.substructureData") }}</h2>
      </div>
      <form class="bg-white p-4 rounded-b space-y-4">
        <InputWrapper :label="$t('lang.fields.name')" content="input" block>
          <InputText v-model="newItem.subName" :name="$t('lang.fields.name')" font-semibold />
        </InputWrapper>
        <InputWrapper
          v-if="idSubStructure"
          :label="$t('lang.msg.factories')"
          content="input"
          block
        >
          <multiselect
            v-model="newItem.factories"
            :options="factories"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :taggable="true"
            tag-placeholder="Escriba para añadir una fábrica"
            placeholder="Escriba para buscar fábricas"
            label="factoryName"
            track-by="idFactory"
            select-label="Pulse enter para seleccionar"
            deselect-label="Pulse enter para eliminar"
            @select="storeFactory"
            @remove="removeFactory"
          />
        </InputWrapper>
        <InputWrapper :label="$t('lang.msg.ubication')" content="input" block>
          <InputText v-model="newItem.subUbication" :name="$t('lang.msg.ubication')" />
        </InputWrapper>
        <div class="flex space-x-4">
          <InputWrapper :label="$t('lang.fields.cif')" content="input" block>
            <InputText v-model="newItem.subCif" :name="$t('lang.fields.cif')" />
          </InputWrapper>
          <InputWrapper :label="$t('lang.fields.phone')" content="input" block>
            <InputText v-model="newItem.subPhone" :name="$t('lang.fields.phone')" />
          </InputWrapper>
        </div>
        <InputWrapper :label="$t('lang.fields.email')" content="input" block>
          <InputText v-model="newItem.subMail" :name="$t('lang.fields.email')" />
        </InputWrapper>
        <InputWrapper :label="$t('lang.fields.address')" content="input" block>
          <textarea
            class="form-textarea w-full"
            v-model="newItem.subDirection"
            :placeholder="$t('lang.fields.address')"
          />
        </InputWrapper>
        <InputWrapper :label="$t('lang.fields.anotations')" content="input" block>
          <textarea
            class="form-textarea w-full"
            v-model="newItem.subAnotations"
            :placeholder="$t('lang.fields.anotations')"
          />
        </InputWrapper>
        <AdminToggleButton v-model="newItem.active" />

        <span class="flex w-full rounded-md shadow-sm sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-primario hover:bg-primario text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="addOrEditItem(`api/SubStructures/Add`, `api/SubStructures/Update`, redirectBack)"
          >{{ newItem.idSubStructure == undefined ? $t('lang.msg.save') : $t('lang.msg.update') }}</button>
        </span>
      </form>
    </section>

    <div class="flex flex-col w-full space-y-8" v-if="newItem.idSubStructure">
      <section>
        <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
          <h2>{{ $t("lang.msg.contactPersons") }}</h2>
        </div>
        <div class="bg-white px-4 pb-2 rounded-b">
          <div
            v-for="(item, index) in contacts"
            :key="`item_${index}`"
            class="py-2 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-contacts gap-2 items-end"
          >
            <InputWrapper :label="$t('lang.fields.name')" content="input" block>
              <InputText v-model="item.contactName" :name="$t('lang.fields.name')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.fields.email')" content="input" block>
              <InputText v-model="item.email" :name="$t('lang.fields.email')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.fields.phone')" content="input" block>
              <InputText v-model="item.phone" :name="$t('lang.fields.phone')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.fields.charge')" content="input" block>
              <InputText v-model="item.cargo" :name="$t('lang.fields.charge')" />
            </InputWrapper>
            <div class="flex justify-between">
              <div class="flex space-x-2">
                <div class="flex space-x-2" v-if="contacts.length > 1 && item.idContactPerson">
                  <ButtonRounded @action="removeContact(index)" color="red">
                    <IconRemove />
                  </ButtonRounded>

                  <ButtonRounded @action="updateContact(item)" color="green">
                    <IconUpdate />
                  </ButtonRounded>
                </div>
                <ButtonRounded v-if="index == contacts.length - 1" @action="addNewContact(item)" color="blue">
                  <IconAdd />
                </ButtonRounded>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
          <h2>{{ $t("lang.msg.processes") }}</h2>
        </div>
        <div class="bg-white p-4 rounded-b">
          <multiselect
            v-model="newItem.processes"
            :options="processes"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            :taggable="true"
            tag-placeholder="Escriba para añadir un proceso"
            placeholder="Escriba para buscar procesos"
            :label="getLangLabel('processName')"
            :track-by="getLangLabel('processName')"
            select-label="Pulse enter para seleccionar"
            deselect-label="Pulse enter para eliminar"
            @select="processSelected"
            @remove="processRemoved"
          >
            <template slot="noOptions">
              La lista de procesos está vacia.
            </template>
          </multiselect>
        </div>
      </section>
      <section>
        <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
          <h2>{{ $t("lang.msg.documents") }}</h2>
        </div>
        <div class="bg-white">
          <vue-dropzone
            ref="dropzone"
            id="dropzone"
            :options="dropzoneOptions"
            @vdropzone-sending="sendingEvent"
          />

          <div class="gallery">
            <ImageGallery
              v-for="(image, imageIndex) in images"
              :key="`image_${imageIndex}`"
              :url="image.url"
              :id-foto="image.idFoto"
              @image-deleted="imageDeleted"
            />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import AdminMixin from "@/components/mixins/AdminMixin";

// DROPZONE
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Multiselect from "vue-multiselect";

export default {
  name: "SubstructureDetail",
  mixins: [AdminMixin],
  components: {
    vueDropzone: vue2Dropzone,
    Multiselect
  },
  data() {
    return {
      idSubStructure: 0,
      params: {},
      factories: [],
      processes: [],
      contacts: [],
      images: [],
      dropzoneOptions: {
        timeout: 120000,
        maxFilesize: 2048,
        url: `${this.$API_URL}/api/DocumentFactories/AddSub`,
        thumbnailWidth: 175,
        thumbnailHeight: 175,
        paramName: "files"
      }
    }
  },
  created() {
    if (this.$route.params.idSubStructure) {
      this.idSubStructure = parseInt(this.$route.params.idSubStructure);
      this.isEditing = true;

      // Set params to fetch the initial data.
      this.params = { idSubStructure: this.idSubStructure };
      this.fetchOneSubstructure();
    }

    this.fetchAllProcesses();
    this.fetchComboFactories();
  },
  methods: {
    fetchOneSubstructure() {
      this.$axios.post("api/SubStructures/PostSubRSC", this.params).then((response) => {
        this.newItem = response.data[0] || {};

        this.fetchSubstructureFactories();
        this.fetchSubstructureProcesses();
        this.fetchSubstructureContacts();
        this.fetchDocuments();
      });
    },
    fetchSubstructureFactories() {
      this.$axios.post("api/SubStructures/GetAllFac", this.params).then((response) => {
        this.$set(this.newItem, 'factories', response.data);
      });
    },
    fetchSubstructureContacts() {
      this.$axios.post("api/ContactPersons/PostSearchIdSubStructure", this.params)
      .then((response) => {
        // At least 1 contact exist in the array.
        response.data.push({});

        this.contacts = response.data;
      });
    },
    fetchSubstructureProcesses() {
      this.$axios.post("api/SubStructures/SearchProcess", this.params).then((response) => {
        this.$set(this.newItem, 'processes', response.data);
      });
    },
    fetchDocuments() {
      this.$axios.post(`api/DocumentFactories/PostDocument`, this.params).then( response => {
        this.images = response.data;
      });
    },
    fetchAllProcesses() {
      this.$axios.get("api/Processes/GetProcessActive").then((response) => {
        this.processes = response.data;
      });
    },
    fetchComboFactories() {
      this.$axios.get("api/DocumentCertificats/ComboFactory").then((response) => {
        this.factories = response.data;
      });
    },
    redirectBack() {
      this.$router.push({ name: "adminSubstructures" });
    },
    contactIsValid(contact) {
      return contact.contactName && contact.email && contact.phone && contact.cargo;
    },
    addNewContact(contact) {
      if (this.contactIsValid(contact)) {
        // Append the idSubStructure to the contact.
        contact.idSubStructure = this.idSubStructure

        this.$axios.post(`api/ContactPersons/Add`, contact).then(() => {
          this.openAlert('success', 'Contacto añadido con éxito', null);
          this.fetchSubstructureContacts(this.params);
          this.contacts.push({});
        });
      } else {
        this.openAlert('info', 'Rellene correctamente el formulario', null);
      }
    },
    updateContact(contact) {
      this.$axios.post(`api/ContactPersons/Update`, contact).then(() => {
        this.openAlert('success', 'Contacto actualizado', null);
      });
    },
    removeContact(index) {
      let contact = this.contacts[index];

      this.$axios.post(`api/ContactPersons/Delete`, contact).then(() => {
        this.openAlert('success', 'Contacto eliminado', null);
      });
      
      this.contacts.splice(index, 1);
    },
    processSelected(item) {
      item.idSubStructure = this.idSubStructure;

      this.$axios.post(`api/SubStructures/AddSubStructuresFactory`, item).then(() => {
        this.fetchSubstructureProcesses(this.params);
        this.openAlert('success', 'Proceso añadido', null);
      });
    },
    processRemoved(event) {
      this.$axios.post(`api/SubStructures/DeleteProcessSubStructure`, {
        idProcessSubStructure: event.idProcessSubStructure
      }).then(() => {
        this.openAlert('success', 'Proceso eliminado', null);
      });
    },
    storeFactory(item) {
      this.$axios.post(`api/SubStructures/AnadirFactory`, {
        idFactory: item.idFactory,
        idSubStructure: this.idSubStructure
      }).then(() => {
        this.fetchSubstructureFactories();
      });
    },
    removeFactory(item) {
      this.$axios.post(`api/SubStructures/DeleteFactory`, item);
    },
    sendingEvent(file, xhr, formData) {
      formData.append("idSubStructure", this.idSubStructure);
    },
    imageDeleted(idFoto) {
      let images = this.images.filter(image => {
        return image.idFoto != idFoto
      });

      this.images = images;
      this.$forceUpdate();
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>